/** The source registry: what is installed, what is enabled, and how each last behaved. */

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type {
  CredentialKey,
  PluginResponse,
  Reliability,
  StoreCredentialRequest,
  UpdatePluginRequest,
} from '@/lib/schemas';
import { Badge, Button, Card, Drawer, ErrorState, Loading, Table } from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

/** Admiralty grades an operator may impose in place of the manifest's. */
const RELIABILITY: readonly Reliability[] = ['A', 'B', 'C', 'D', 'E', 'F'] as const;

/** The plugin registry view. */
export function PluginsPage(): ReactElement {
  const canManage = useCan('manage_plugins');
  const canManageCredentials = useCan('manage_credentials');
  const queryClient = useQueryClient();
  const [configuring, setConfiguring] = useState<PluginResponse | null>(null);

  const plugins = useQuery({
    queryKey: ['plugins'],
    queryFn: () => request<PluginResponse[]>('/plugins'),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['plugins'] });
  };

  const probe = useMutation({
    mutationFn: (name: string) =>
      request<unknown>(`/plugins/${name}:health-check`, { method: 'POST' }),
    onSuccess: refresh,
  });

  // Enabling sends `acknowledge_legal_class` because a `TOS_RESTRICTED` source refuses
  // without it. v1 ships none, so the flag is presently always accepted — but sending it
  // silently would be wrong, which is why the button says what it is agreeing to.
  const setEnabled = useMutation({
    mutationFn: ({ plugin, enabled }: { plugin: PluginResponse; enabled: boolean }) =>
      request<PluginResponse, UpdatePluginRequest>(`/plugins/${plugin.name}`, {
        method: 'PATCH',
        body: { enabled, acknowledge_legal_class: enabled },
      }),
    onSuccess: refresh,
  });

  const setReliability = useMutation({
    mutationFn: ({ plugin, grade }: { plugin: PluginResponse; grade: Reliability | null }) =>
      request<PluginResponse, UpdatePluginRequest>(`/plugins/${plugin.name}`, {
        method: 'PATCH',
        body: {
          enabled: plugin.enabled,
          acknowledge_legal_class: plugin.enabled,
          source_reliability_override: grade,
        },
      }),
    onSuccess: refresh,
  });

  if (plugins.isLoading) return <Loading />;
  if (plugins.error) return <ErrorState error={plugins.error} />;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Sources</h1>
      <p className="text-sm text-muted">
        Installing a package makes a source discoverable, not usable. Each is enabled
        deliberately (ADR-0002), and a health check is queued rather than performed here —
        the API container makes no outbound requests.
      </p>

      {setEnabled.error ? <ErrorState error={setEnabled.error} /> : null}

      <Table
        headers={['Source', 'Version', 'Class', 'Reliability', 'Enabled', 'Health', '']}
        empty="No sources are installed."
        rows={(plugins.data ?? []).map((plugin) => [
          <span key="n" className="font-mono text-xs">
            {plugin.name}
          </span>,
          <span key="v" className="font-mono text-xs text-muted">
            {plugin.version}
          </span>,
          <Badge key="l">{plugin.legal_class}</Badge>,
          <span key="r" className="flex items-center gap-1">
            <Badge tone="accent">{plugin.source_reliability}</Badge>
            {plugin.reliability_overridden ? (
              <span className="text-xs text-muted" title="Overridden by this tenant">
                overridden
              </span>
            ) : null}
          </span>,
          <Badge key="e" tone={plugin.enabled ? 'ok' : 'muted'}>
            {plugin.enabled ? 'enabled' : 'disabled'}
          </Badge>,
          <span key="h" className="flex flex-col">
            <Badge tone={plugin.health_status === 'healthy' ? 'ok' : 'warn'}>
              {plugin.health_status ?? 'unknown'}
            </Badge>
          </span>,
          canManage ? (
            <span key="a" className="flex gap-2">
              <Button
                onClick={() =>
                  setEnabled.mutate({ plugin, enabled: !plugin.enabled })
                }
                disabled={setEnabled.isPending}
              >
                {plugin.enabled ? 'Disable' : 'Enable'}
              </Button>
              <Button onClick={() => probe.mutate(plugin.name)} disabled={probe.isPending}>
                Check
              </Button>
              <Button onClick={() => setConfiguring(plugin)}>Configure</Button>
            </span>
          ) : (
            <span key="a" />
          ),
        ])}
      />

      {configuring ? (
        <Drawer open title={`Configure ${configuring.name}`} onClose={() => setConfiguring(null)}>
          <div className="space-y-6">
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Source reliability</h3>
              <p className="text-xs text-muted">
                The Admiralty grade every claim from this source carries. The manifest sets
                a default; an operator who trusts the source less than its author does says
                so here, and the pipeline honours it on each assertion.
              </p>
              <div className="flex flex-wrap gap-2">
                {RELIABILITY.map((grade) => (
                  <Button
                    key={grade}
                    onClick={() => setReliability.mutate({ plugin: configuring, grade })}
                    disabled={setReliability.isPending}
                  >
                    {grade}
                  </Button>
                ))}
                <Button
                  onClick={() => setReliability.mutate({ plugin: configuring, grade: null })}
                  disabled={setReliability.isPending}
                >
                  Use manifest default
                </Button>
              </div>
            </section>

            {canManageCredentials ? (
              <CredentialsPanel pluginName={configuring.name} />
            ) : (
              <p className="text-sm text-muted">
                Managing credentials requires the <code>manage_credentials</code>{' '}
                permission, which your role does not hold.
              </p>
            )}
          </div>
        </Drawer>
      ) : null}
    </div>
  );
}

/**
 * Credentials for one source.
 *
 * **Values are never displayed, under any role.** The API returns key names and last-use
 * times only; there is no endpoint that reveals a stored secret and this panel does not
 * ask for one. Storing a value is therefore always a write of something the operator
 * already has, never an edit of something shown back to them.
 */
function CredentialsPanel({ pluginName }: { pluginName: string }): ReactElement {
  const queryClient = useQueryClient();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');

  const credentials = useQuery({
    queryKey: ['plugin-credentials', pluginName],
    queryFn: () => request<CredentialKey[]>(`/plugins/${pluginName}/credentials`),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['plugin-credentials', pluginName] });
  };

  const store = useMutation({
    mutationFn: () =>
      request<void, StoreCredentialRequest>(`/plugins/${pluginName}/credentials`, {
        method: 'PUT',
        // `satisfies` because `RequestOptions.body` is `unknown`, so nothing here was
        // checked against the API at all: this sent `credential_value` where the schema
        // says `value`, and every attempt to store a plugin credential through this form
        // was rejected as a validation failure. The generated types existed the whole
        // time; no call site was asking them anything.
        body: { credential_key: key, value } satisfies StoreCredentialRequest,
      }),
    onSuccess: () => {
      setKey('');
      setValue('');
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (credentialKey: string) =>
      request<void>(`/plugins/${pluginName}/credentials/${credentialKey}`, {
        method: 'DELETE',
      }),
    onSuccess: refresh,
  });

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">Credentials</h3>
      <p className="text-xs text-muted">
        Stored encrypted and never returned. A source only ever receives the keys its
        manifest declares.
      </p>

      {credentials.error ? <ErrorState error={credentials.error} /> : null}
      {store.error ? <ErrorState error={store.error} /> : null}

      <Table
        headers={['Key', 'Last used', '']}
        empty="No credentials are configured."
        rows={(credentials.data ?? []).map((entry) => [
          <span key="k" className="font-mono text-xs">
            {entry.credential_key}
          </span>,
          <span key="u" className="text-xs text-muted">
            {entry.last_used_at ? new Date(entry.last_used_at).toLocaleString() : 'never'}
          </span>,
          <Button
            key="d"
            onClick={() => remove.mutate(entry.credential_key)}
            disabled={remove.isPending}
          >
            Delete
          </Button>,
        ])}
      />

      <Card>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col text-xs">
            Key
            <input
              className="rounded border px-2 py-1 font-mono text-xs"
              value={key}
              onChange={(event) => setKey(event.target.value)}
              placeholder="api_token"
            />
          </label>
          <label className="flex flex-col text-xs">
            Value
            <input
              className="rounded border px-2 py-1 font-mono text-xs"
              type="password"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder="write only"
            />
          </label>
          <Button onClick={() => store.mutate()} disabled={!key || !value || store.isPending}>
            Store
          </Button>
        </div>
      </Card>
    </section>
  );
}
